import React, {FC, useCallback, useEffect, useState} from 'react'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {
  createCorporate,
  deleteCorporate,
  loadCorporates,
  setCorporateActive,
  updateCorporate,
} from './Api'
import {ICorporateModel, ICorporateRequest} from './CorporateModel'
import {CorporateTable} from './CorporateTable'
import {CorporateFormModal} from './CorporateFormModal'
import {CorporateUsersModal} from './CorporateUsersModal'

/** Admin: firma oluşturma, güncelleme, pasife alma ve silme */
const CorporatePage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [corporates, setCorporates] = useState<ICorporateModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState<ICorporateModel | null>(null)
  const [deletingItem, setDeletingItem] = useState<ICorporateModel | null>(null)
  const [togglingItem, setTogglingItem] = useState<ICorporateModel | null>(null)
  const [usersItem, setUsersItem] = useState<ICorporateModel | null>(null)

  const fetchCorporates = useCallback(async (page: number) => {
    setLoading(true)
    try {
      const payload = (await loadCorporates(page)).data.payload
      setCorporates(payload.content)
      setTotalPages(payload.totalPages)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Firmalar yüklenirken hata oluştu'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCorporates(0)
  }, [fetchCorporates])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchCorporates(newPage)
      }
    },
    [totalPages, fetchCorporates]
  )

  const handleCreateClick = () => {
    setEditingItem(null)
    setShowForm(true)
  }

  const handleEdit = useCallback((corporate: ICorporateModel) => {
    setEditingItem(corporate)
    setShowForm(true)
  }, [])

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingItem(null)
  }

  const handleSubmit = async (request: ICorporateRequest) => {
    setSaving(true)
    try {
      if (editingItem?.id) {
        await updateCorporate(editingItem.id, request)
        showSuccessToast('Firma başarıyla güncellendi')
        await fetchCorporates(currentPage)
      } else {
        await createCorporate(request)
        showSuccessToast('Firma oluşturuldu. Kullanıcı bağlamayı unutmayınız.')
        setCurrentPage(0)
        await fetchCorporates(0)
      }
      handleCloseForm()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Bir hata oluştu'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async () => {
    if (!togglingItem?.id) return
    const activate = !togglingItem.active
    setLoading(true)
    try {
      await setCorporateActive(togglingItem.id, activate)
      showSuccessToast(activate ? 'Firma aktifleştirildi' : 'Firma pasife alındı')
      await fetchCorporates(currentPage)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Durum değiştirilemedi'))
    } finally {
      setTogglingItem(null)
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingItem?.id) return
    setLoading(true)
    try {
      await deleteCorporate(deletingItem.id)
      showSuccessToast('Firma silindi')
      await fetchCorporates(currentPage)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Firma silinirken hata oluştu'))
    } finally {
      setDeletingItem(null)
      setLoading(false)
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Firma Yönetimi</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Firmalar</span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              Platformu kullanan müşteri firmalar ve kullanıcıları
            </span>
          </h3>
          <div className='card-toolbar'>
            <button type='button' className='btn btn-sm btn-primary' onClick={handleCreateClick}>
              <i className='bi bi-plus-lg fs-5'></i>
              Yeni Firma
            </button>
          </div>
        </div>
        <div className='card-body pt-0'>
          <CorporateTable
            data={corporates}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onUsers={setUsersItem}
            onToggleActive={setTogglingItem}
            onDelete={setDeletingItem}
            loading={loading}
          />
        </div>
      </div>

      <CorporateFormModal
        show={showForm}
        editingItem={editingItem}
        loading={saving}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
      />

      <CorporateUsersModal corporate={usersItem} onClose={() => setUsersItem(null)} />

      <ConfirmModal
        show={!!togglingItem}
        header={togglingItem?.active ? 'Pasife Alma Onayı' : 'Aktifleştirme Onayı'}
        text={
          togglingItem?.active
            ? `"${togglingItem?.name ?? ''}" pasife alınırsa kullanıcıları sisteme erişemez ve kariyer sayfası kapanır. Devam edilsin mi?`
            : `"${togglingItem?.name ?? ''}" tekrar aktifleştirilsin mi?`
        }
        handleClose={() => setTogglingItem(null)}
        applyOnClick={handleToggleActive}
      />

      <ConfirmModal
        show={!!deletingItem}
        header='Silme Onayı'
        text={`"${deletingItem?.name ?? ''}" firmasını silmek istediğinize emin misiniz? Firma kullanıcıları sisteme erişemez.`}
        handleClose={() => setDeletingItem(null)}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </>
  )
}

export {CorporatePage}
